import { useEffect, useMemo, useState } from "react";
import DepartmentTable from "@/components/userAdministration/departmenttable";
import UserTable from "@/components/userAdministration/userTable/userTable";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Chip,
  CircularProgress,
  Grid,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useTranslation } from "react-i18next";
import FileUploadInput from "@/components/forms/inputs/upload";
import "@/components/userAdministration/userAdministration.css";
import CsvExport, {
  KAER_WORKER_CSV_TEMPLATE,
} from "@/components/userAdministration/csvExport";
import Modal from "@/components/modal";
import CompanyManagerTable from "@/components/userAdministration/companyManagerTable";
import styles from "@/components/userAdministration/administration.module.css";
import GenericButton from "@/components/forms/inputs/button/genericButton";
import { useErrorModal } from "@/components/forms/errorHandling/useErrorModal";
import GenericNotification from "@/components/notification/genericNotification";
import { useConfirmationModal } from "@/context/confirmationModalContext";
import { useSelector } from "react-redux";
import { RootState } from "@/components/state/store";
import useFocusFirstInvalidField from "@/helper/customHooks/useFocusFirstInvalidField";
import { apiRequest } from "@/helper/api";
import type { FileUploadRequestOptions } from "@/helper/customHooks/useFileUploadProgress";
import CsvUploadSteps from "@/components/userAdministration/csvUploadSteps";
import WorkerCsvImportRunningPanel from "@/components/userAdministration/workerCsvImportRunningPanel";
import type { RunningWorkerCsvImport } from "@/components/userAdministration/useWorkerCsvImportTracking";

interface Props {
  companySubjectId?: string;
  companyId?: string;
  uploadSuccessful?: boolean;
  handleSendRegistrationEmail?: (
    row: any,
    skipQuestion?: false | undefined
  ) => void;
  handleUnblockUser?: (row: any, skipQuestion?: false | undefined) => void;
  handleUndeleteUser?: (
    row: any,
    reset: () => void,
    skipQuestion?: false | undefined
  ) => void;
  subdomain?: string;
  handleUploadSuccess?: () => void;
  handleEntries?: (entries: any) => void;
  selectedChildCompany?: any;
  handleTaskTrackingId?: (id: string) => void;
  handleDepartmentResults?: (results: any) => void;
  isEntriesModalOpen: boolean;
  runningWorkerCsvImport?: RunningWorkerCsvImport | null;
}

export type Department = {
  departmentName: string;
  departmentId: string;
};

const CSV_UPLOAD_MODAL_WIDTH = "40rem";

export default function Administration(props: Props) {
  const { t } = useTranslation();
  const [department, setDepartment] = useState<Department[]>([]);
  const [hasDepartments, setHasDepartments] = useState<boolean>(true);
  const [exportModalOpen, setExportModalOpen] = useState<boolean>(false);
  const [uploadInProgress, setUploadInProgress] = useState(false);
  const [csvFileExportTarget, setCsvFileExportTarget] = useState<string>("");
  const [csvExportTargetError, setCsvExportTargetError] = useState(false);
  const { registerValidationTarget, focusFirstInvalidField } =
    useFocusFirstInvalidField<Record<string, never>, "csvExportTarget">({
      fieldOrder: [],
    });
  const [userUploadModalOpen, setUserUploadModalOpen] =
    useState<boolean>(false);
  const [departmentUploadModalOpen, setDepartmentUploadModalOpen] =
    useState<boolean>(false);
  const [
    triggerCompanyManagerTableReload,
    setTriggerCompanyManagerTableReload,
  ] = useState(false);
  const [triggerUserTableReload, setTriggerUserTableReload] = useState(false);
  const [triggerDepartmentTableReload, setTriggerDepartmentTableReload] =
    useState(false);
  const [isUploadExpanded, setIsUploadExpanded] = useState<boolean>(false);
  const [isDepartmentExpanded, setIsDepartmentExpanded] =
    useState<boolean>(false);
  const [isUserExpanded, setIsUserExpanded] = useState<boolean>(true);
  const [isCompanyManagerExpanded, setIsCompanyManagerExpanded] =
    useState<boolean>(false);
  const [refreshUserTable, setRefreshUSerTable] = useState(0);
  const { handleError, errorModal } = useErrorModal();

  const [showNotification, setShowNotification] = useState<boolean>(false);
  const [notificationMessage, setNotificationMessage] = useState<any>("");
  const [notificationVariant, setNotificationVariant] = useState<
    "success" | "error" | "warning" | "info" | undefined
  >();

  const { showConfirmationModal } = useConfirmationModal();

  const user = useSelector((state: RootState) => state.login);

  const logedInCompanySubjectId = useSelector(
    (state: RootState) => state.parent?.subjectId
  );

  const currentCompanySubjectId = logedInCompanySubjectId || user?.subjectId;

  const currentCompanyAssignment = useMemo(() => {
    return user?.assigned_companies?.find(
      (entry: any) => entry?.company_subject?.id === currentCompanySubjectId
    );
  }, [user?.assigned_companies, currentCompanySubjectId]);
  const currentManagerType = currentCompanyAssignment?.manager_type ?? null;

  const resetStateOnCloseNotification = () => {
    setShowNotification(false);
    setNotificationMessage("");
  };

  useEffect(() => {
    if (!hasDepartments) {
      setIsDepartmentExpanded(true);
      setIsUserExpanded(false);
    }
  }, [hasDepartments]);

  const handleRefreshUserTable = () => {
    setRefreshUSerTable((prev) => prev + 1);
  };

  const handleDepartmentForFilter = (row: any) => {
    setDepartment([
      {
        departmentName: row.original.name,
        departmentId: row.original.id,
      },
    ]);
  };
  const handleHasDepartments = (value: boolean) => {
    setHasDepartments(value);
  };

  const customCreateHandler = () => {
    if (hasDepartments) {
      return;
    }
    return showConfirmationModal(t("userTable_no_departments"), {
      disableCancelButton: true,
    });
  };

  const handleToggleUpload = () => setIsUploadExpanded((prev) => !prev);
  const handleToggleDepartment = () => setIsDepartmentExpanded((prev) => !prev);
  const handleToggleUser = () => setIsUserExpanded((prev) => !prev);
  const handleToggleCompanyManager = () =>
    setIsCompanyManagerExpanded((prev) => !prev);

  const handleDeleteDepartment = () => {
    setDepartment([]);
  };

  const isAdmin = props.subdomain === "admin";
  const isManage = props.subdomain === "manage";
  const exportCompanyId = isAdmin
    ? props.companyId
    : props.selectedChildCompany?.companyId ||
      currentCompanyAssignment?.company_subject?.company_id;
  const templateSubjectId = isAdmin
    ? props.companySubjectId
    : props.selectedChildCompany?.subjectId;
  const csvUploadAvailable =
    isManage || (isAdmin && Boolean(props.companySubjectId));

  const downloadWorkerTemplate = () => {
    window.location.href = `/api/v1/user/example_worker_csv/?impersonate_subject=${String(
      templateSubjectId
    )}`;
  };

  const downloadDepartmentTemplate = () => {
    window.location.href = `/api/v1/department/example_csv/?impersonate_subject=${String(
      templateSubjectId
    )}`;
  };

  const closeUserUploadModal = () => {
    setUserUploadModalOpen(false);
  };

  const closeDepartmentUploadModal = () => {
    setDepartmentUploadModalOpen(false);
  };

  const buildCsvFormData = (
    file: File,
    { onUploadProgress }: FileUploadRequestOptions
  ) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("original_filename", file.name);
    if (props.companySubjectId) {
      formData.append("impersonate_subject", props.companySubjectId);
    }
    if (logedInCompanySubjectId) {
      formData.append("impersonate_subject", String(logedInCompanySubjectId));
    }
    return {
      submitData: formData,
      config: {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress,
      },
      disableGlobalSpinner: true,
      method: "post" as const,
    };
  };

  const uploadWorkerCsv = async (
    files: File[],
    options: FileUploadRequestOptions
  ) => {
    const response = await apiRequest<{ task_tracking_id?: string }>({
      entity: "user",
      additionalRouteParts: { worker_csv: "/" },
      ...buildCsvFormData(files[0], options),
    });
    props.handleTaskTrackingId?.(response.data?.task_tracking_id ?? "");
  };

  const uploadDepartmentCsv = async (
    files: File[],
    options: FileUploadRequestOptions
  ) => {
    const response = await apiRequest({
      entity: "department",
      additionalRouteParts: { create_from_csv: "/" },
      ...buildCsvFormData(files[0], options),
    });
    props.handleDepartmentResults?.(response.data);
  };

  useEffect(() => {
    if (
      props.isEntriesModalOpen ||
      exportModalOpen ||
      userUploadModalOpen ||
      departmentUploadModalOpen
    ) {
      setIsUserExpanded(false);
    }
  }, [
    props.isEntriesModalOpen,
    exportModalOpen,
    userUploadModalOpen,
    departmentUploadModalOpen,
  ]);

  const userUploadModalContent = props.runningWorkerCsvImport ? (
    <WorkerCsvImportRunningPanel
      startedAt={props.runningWorkerCsvImport.startedAt}
      onClose={closeUserUploadModal}
    />
  ) : (
    <CsvUploadSteps
      downloadTemplateLabel={t("download_example_csv")}
      onDownloadTemplate={downloadWorkerTemplate}
      uploadInput={
        <FileUploadInput
          multiple={false}
          name="worker_csv"
          accept={[".csv"]}
          customUploadHandler={uploadWorkerCsv}
          onCancel={closeUserUploadModal}
          onUploadStateChange={setUploadInProgress}
          handleUploadSuccess={closeUserUploadModal}
        />
      }
    />
  );

  const departmentUploadModalContent = (
    <CsvUploadSteps
      downloadTemplateLabel={`${t("download_example_csv")} - ${t(
        "departments"
      )}`}
      onDownloadTemplate={downloadDepartmentTemplate}
      uploadInput={
        <FileUploadInput
          multiple={false}
          name="department_csv"
          accept={[".csv"]}
          customUploadHandler={uploadDepartmentCsv}
          onCancel={closeDepartmentUploadModal}
          onUploadStateChange={setUploadInProgress}
          handleUploadSuccess={closeDepartmentUploadModal}
        />
      }
    />
  );

  const uploadExportHeading = () => {
    if (isAdmin || isManage) {
      return t("csv_upload") + " / " + t("csv_export");
    } else return t("csv_upload");
  };
  let userHeading = <h1 className="tableHeading">{t("customer")}</h1>;
  const closedUsertableHeading = t("customer");

  if (department?.length !== 0) {
    userHeading = (
      <h1 className="tableHeading_withChip">
        {t("customer")}
        <Chip
          label={department?.[0].departmentName}
          onDelete={handleDeleteDepartment}
          className={styles.chip}
          classes={{ deleteIcon: styles.deleteIcon }}
        />
      </h1>
    );
  }

  const handleGetSelectedExportTemplate = (template: string) => {
    setCsvFileExportTarget(template);
    setCsvExportTargetError(false);
  };

  const handleDownloadCsv = () => {
    if (csvFileExportTarget === "") {
      setCsvExportTargetError(true);
      focusFirstInvalidField({}, ["csvExportTarget"]);
      return;
    }

    if (!exportCompanyId) return;

    const params = new URLSearchParams({
      company_id: String(exportCompanyId),
      template: csvFileExportTarget,
    });
    if (isManage && props.selectedChildCompany?.subjectId) {
      params.set(
        "impersonate_subject",
        String(props.selectedChildCompany.subjectId)
      );
    }
    if (department.length !== 0) {
      params.set("department", department[0].departmentId);
    }

    window.location.href = `/api/v1/user/export_worker_csv/?${params.toString()}`;
  };

  const handleNotification = (message: any, variant: any, show: boolean) => {
    setShowNotification(show);
    setNotificationMessage(message);
    setNotificationVariant(variant);
  };

  return (
    <Grid container>
      <Grid item xs={12} className={styles.gridContainer}>
        <Accordion
          className={styles.accordion}
          expanded={isDepartmentExpanded}
          onChange={handleToggleDepartment}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            classes={{ content: styles.accordionSummaryContent }}
          >
            {!isDepartmentExpanded ? (
              t("departments")
            ) : (
              <h1 className="tableHeading">{t("departments")}</h1>
            )}
          </AccordionSummary>
          <AccordionDetails>
            <DepartmentTable
              companySubjectId={props.companySubjectId}
              companyId={props.companyId}
              uploadSuccessful={
                Boolean(props.uploadSuccessful) !== triggerDepartmentTableReload
              }
              handleDepartmentForFilter={handleDepartmentForFilter}
              department={department}
              handleDeleteDepartment={handleDeleteDepartment}
              handleHasDepartments={handleHasDepartments}
              subdomain={props.subdomain}
              handleRefreshUserTable={handleRefreshUserTable}
              handleError={handleError}
              handleNotification={handleNotification}
            />
          </AccordionDetails>
        </Accordion>
      </Grid>

      <Grid item xs={12} className={styles.gridContainer}>
        <Accordion
          className={styles.accordion}
          expanded={isCompanyManagerExpanded}
          onChange={handleToggleCompanyManager}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            classes={{ content: styles.accordionSummaryContent }}
          >
            {!isCompanyManagerExpanded ? (
              t("manager")
            ) : (
              <h1 className="tableHeading">{t("manager")}</h1>
            )}
          </AccordionSummary>
          <AccordionDetails>
            <CompanyManagerTable
              currentManagerType={currentManagerType}
              companySubjectId={props.companySubjectId}
              companyId={props.companyId}
              subdomain={props.subdomain}
              triggerReload={triggerCompanyManagerTableReload}
              onManagersChanged={() =>
                setTriggerUserTableReload((prev) => !prev)
              }
            />
          </AccordionDetails>
        </Accordion>
      </Grid>

      <Grid item xs={12} className={styles.gridContainer}>
        <Accordion
          className={styles.accordion}
          expanded={isUploadExpanded}
          onChange={handleToggleUpload}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            classes={{ content: styles.accordionSummaryContent }}
          >
            {!isUploadExpanded ? (
              uploadExportHeading()
            ) : (
              <h1 className="tableHeading">{uploadExportHeading()}</h1>
            )}
          </AccordionSummary>
          <AccordionDetails>
            <Grid container columnSpacing={"1rem"} rowSpacing={"0.5rem"}>
              <Grid item className={styles.uploadActionItem}>
                <GenericButton
                  onClick={() => {
                    setUserUploadModalOpen(true);
                  }}
                >
                  {t("csv_customerupload")}
                </GenericButton>
                {props.runningWorkerCsvImport && (
                  <Chip
                    size="small"
                    icon={<CircularProgress size={14} color="inherit" />}
                    label={t("worker_csv_import_running_title")}
                    className={styles.runningImportChip}
                  />
                )}
              </Grid>
              <Grid item>
                <GenericButton
                  onClick={() => {
                    setDepartmentUploadModalOpen(true);
                  }}
                >
                  {t("csv_departmentupload")}
                </GenericButton>
              </Grid>
              {(isAdmin || isManage) && (
                <Grid item>
                  <GenericButton
                    disabled={!exportCompanyId}
                    onClick={() => {
                      setCsvFileExportTarget(
                        isManage ? KAER_WORKER_CSV_TEMPLATE : ""
                      );
                      setExportModalOpen(true);
                      setCsvExportTargetError(false);
                    }}
                  >
                    {t("csv_export")}
                  </GenericButton>
                </Grid>
              )}
            </Grid>
          </AccordionDetails>
        </Accordion>
      </Grid>
      <Grid item xs={12} className={styles.gridContainer}>
        <Accordion
          className={styles.accordion}
          expanded={isUserExpanded}
          onChange={handleToggleUser}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            classes={{ content: styles.accordionSummaryContent }}
          >
            {!isUserExpanded ? closedUsertableHeading : userHeading}
          </AccordionSummary>
          <AccordionDetails>
            <UserTable
              key={refreshUserTable}
              companySubjectId={props.companySubjectId}
              companyId={props.companyId}
              uploadSuccessful={props.uploadSuccessful}
              handleSendRegistrationEmail={props.handleSendRegistrationEmail}
              handleUnblockUser={props.handleUnblockUser}
              department={department}
              handleUndeleteUser={props.handleUndeleteUser}
              handleUploadSuccess={props.handleUploadSuccess}
              customCreateHandler={!hasDepartments ? customCreateHandler : null}
              triggerCompanyManagerTableReload={() => {
                setTriggerCompanyManagerTableReload((prev) => !prev);
                setTriggerDepartmentTableReload((prev) => !prev);
              }}
              triggerReload={triggerUserTableReload}
            />
          </AccordionDetails>
        </Accordion>
      </Grid>
      <Modal
        open={exportModalOpen}
        onClose={() => {
          setExportModalOpen(false);
          setCsvExportTargetError(false);
        }}
        title={t("csv_export")}
        contentClassName={styles.modalContent}
        buttonPlacement="space-evenly"
        submitButtonText={t("download")}
        onSubmit={handleDownloadCsv}
      >
        <Grid container columnSpacing={"1rem"} rowSpacing={"1rem"}>
          {department.length !== 0 && (
            <Grid item xs={12}>
              <Chip
                label={department?.[0].departmentName}
                onDelete={handleDeleteDepartment}
                className={styles.chip}
                classes={{ deleteIcon: styles.deleteIcon }}
              />
            </Grid>
          )}
          <Grid item xs={12}>
            <div ref={registerValidationTarget("csvExportTarget")}>
              <CsvExport
                value={csvFileExportTarget}
                onChange={handleGetSelectedExportTemplate}
                kaerFormatOnly={isManage}
              />
              {csvExportTargetError && (
                <span role="alert">{t("field_required")}</span>
              )}
            </div>
          </Grid>
        </Grid>
      </Modal>

      <Modal
        open={userUploadModalOpen}
        onClose={closeUserUploadModal}
        title={t("customer") + " " + t("csv_upload")}
        contentClassName={styles.modalContent}
        hideDialogActions={true}
        disableCancelButton={uploadInProgress}
        maxWidth={CSV_UPLOAD_MODAL_WIDTH}
      >
        {csvUploadAvailable && userUploadModalContent}
      </Modal>

      <Modal
        open={departmentUploadModalOpen}
        onClose={closeDepartmentUploadModal}
        title={t("departments") + " " + t("csv_upload")}
        contentClassName={styles.modalContent}
        hideDialogActions={true}
        disableCancelButton={uploadInProgress}
        maxWidth={CSV_UPLOAD_MODAL_WIDTH}
      >
        {csvUploadAvailable && departmentUploadModalContent}
      </Modal>
      {errorModal}
      <GenericNotification
        message={showNotification ? notificationMessage : null}
        variant={notificationVariant}
        handleCloseSnackbar={resetStateOnCloseNotification}
      />
    </Grid>
  );
}
